import {addHandler, windowScroll, windowSize} from './scroll_resize'
import {PHONE_MAX} from '../src/constants'

/**
 * Gets the top/left offset of an object in relation to the window
 *
 * @function getOffset
 * @param {HtmlElement} selector
 * @returns {object} left/right
**/

export function getOffset (el) {
  el = el.getBoundingClientRect()
  return {
    left: el.left + window.scrollX,
    top: el.top + window.scrollY
  }
}

/**
 * Finds the closest ancestor of any of el which matches selector.
 *
 * @function closest
 * @param {HtmlElement} selector
 * @returns {HtmlElement}
**/

export function closest (el, sel) {
  if (typeof el.closest === 'function') {
    return el.closest(sel) || null
  }
  while (el) {
    if (el.matches(sel)) {
      return el
    }
    el = el.parentElement
  }
  return null
}

/**
 * Basic class toggle method with callback
 *
 * @function toggle
 * @param {HtmlElement} el
 * @param {selector} className
 * @param {function} callback
 * @param {class/function} that parent (this)
 * @returns {HtmlElement}
 * @example
 * const sampleCallback = (bool) => {
 *   alert(bool)
 * }
 * toggle(document.body, 'hello-world', sampleCallback)
**/

export function toggle (el, className, callback, that) {
  let bool
  if (!el.classList.contains(className)) {
    el.classList.add(className)
    bool = true
  } else {
    el.classList.remove(className)
    bool = false
  }
  if (callback) {
    return callback(bool, that)
  }
}

/**
 * Selects the next item in a nodelist,
 * if there is none return the first in list
 *
 * @function next
 * @param {HtmlElement} selector
 * @param {bool} loop
 * @returns {HtmlElement} next or first
**/

export function next (el, loop = true) {
  if (loop) {
    return el.nextElementSibling ? el.nextElementSibling : el.parentElement.firstElementChild
  } else {
    return el.nextElementSibling
  }
}

/**
 * Selects the prev item in a nodelist,
 * if there is none return the last in list
 *
 * @function prev
 * @param {HtmlElement} selector
 * @param {bool} loop
 * @returns {HtmlElement} next or first
**/

export function prev (el, loop = true) {
  if (loop) {
    return el.previousElementSibling ? el.previousElementSibling : el.parentElement.lastElementChild
  } else {
    return el.previousElementSibling
  }
}

/**
 * add subtle parallax movement to an element,
 * @function parallax
 * @param  {Array} el               element to move
 * @param  {Number} [factor=0.5]     controls effect amount
 * @param  {Number} [mobileFactor=0]     controls effect amount on mobile
 * @return {Object} object with resize and scroll props
 * TODO - handle relative/fixed differently
 */

export function parallax (el, options = {}) {
  const {
    factor = 0.5,
    mobileFactor = 0
  } = options

  let trigger
  let winWidth
  let initialised = false
  let resizeTimer

  const resize = (ww, wh) => {
    const transform = el.style.transform.match(/translateY\((-?[\d.]+)px\)/)
    trigger = getOffset(el).top
    // trigger = 0

    if (transform) {
      trigger -= parseInt(transform[1])
    }

    winWidth = ww
    clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(() => {
      const ws = windowScroll()
      scroll(ws.top, ws.left)
    }, 250)
  }

  const scroll = (st, sl) => {
    // relative
    // const shift = (trigger - st) *
    //   (winWidth > PHONE_MAX ? factor : mobileFactor)
    // fixed
    const shift = (0 - st) *
      (winWidth > PHONE_MAX ? factor : mobileFactor)

    if (!initialised) {
      el.classList.add('parallax-initialised')
      initialised = true
    }

    // DISABLED limit to avoid unnecessary DOM updates
    // if (shift < winHeight && shift > -winHeight) {
    let transform = 'translateY(' + shift + 'px) translateZ(0)'
    el.style[window.Modernizr.prefixed('transform')] = transform
    // }
  }

  addHandler({
    resize: resize,
    scroll: scroll
  }, true)
}

/**
 * while scrolling adjust a percent or decimal value
 * according to a factor of the scroll, values will only vary from
 * 0-100 (percent) or 0-1 (decimal)
 * @function relativeScroll
 * @param  {Array} el               element to move
 * @param  {Decimal} [factor=0.5]     controls effect amount
 * @param  {Decimal} [mobileFactor=0]     controls effect amount on mobile
 * @param  {Decimal} [triggerPoint=0]     scroll position to start (percent of window)
 * @param  {boolean} [isDecimal=false]     percent/decimal
 * @param  {string} [property='transform']   eg. el.style[property]

 * @return {Object} object with resize and scroll props
 * TODO - bit hairy with triggerpoint + factor ??
 *        allow for transform
 */

export function relativeScroll (el, options = {}) {
  const {
    factor = 0.5,
    mobileFactor = 0,
    triggerPoint = 0,
    isDecimal = false,
    property = 'transform'
  } = options

  let trigger
  let winWidth
  let initialised = false
  let resizeTimer

  const min = 0
  const max = 100

  const resize = (ww, wh) => {
    trigger = windowSize().height * triggerPoint
    winWidth = ww
    clearTimeout(resizeTimer)
    resizeTimer = window.setTimeout(() => {
      const ws = windowScroll()
      scroll(ws.top, ws.left)
    }, 250)
  }

  const scroll = (st, sl) => {
    let shift = (st - trigger) *
      (winWidth > PHONE_MAX ? factor : mobileFactor)

    shift = Math.min(shift, max)
    shift = Math.max(shift, min)

    if (!initialised) {
      el.classList.add('scroll-relative-initialised')
      initialised = true
    }
    // if (shift >= min && shift <= max) {
    let transform = isDecimal ? (shift / max) : `${shift}%`
    el.style[window.Modernizr.prefixed(property)] = transform
    // }
  }

  addHandler({
    resize: resize,
    scroll: scroll
  }, true)
}

/**
 * Just wait to show image untill it's loaded.
 * return ... adds suitable classes to elements
 *
 * @param {HtmlElement} .image
**/

export function fakeLazyLoad (el) {
  if (!el.classList.contains('lazyloading') &&
      !el.classList.contains('lazyloaded')) {
    const im = el.querySelector('img')
    const src = im.getAttribute('data-src')
    const srcset = im.getAttribute('data-srcset')

    el.classList.add('lazyloading')

    if (src) {
      im.setAttribute('src', src)
      im.removeAttribute('data-src')
    }
    if (srcset) {
      im.setAttribute('srcset', srcset)
      im.removeAttribute('data-srcset')
    }

    im.addEventListener('load', function (e) {
      el.classList.remove('lazyloading')
      el.classList.add('lazyloaded')
    })
  }
}

/**
 * add/remove a class at a specific scroll point
 * all resize control is handled here
 *
 * @param {HtmlElement/number} trigger
 * @param {string} className
 * @param {boolean} remove (do you want to remove the class again?)
 * @param {selector} applyClass (can be body or selector)
 * @param {decimal} buffer (a percent of the screen height eg. .5 = half)
 * @param {boolean} allowMobile (do you want it to track on mobile?)
**/

export function scrollPointTrigger (trigger, options = {}) {
  const {
    className = 'fixed',
    remove = true,
    applyClass = trigger,
    buffer = false,
    allowMobile = false
  } = options

  let triggerPoint
  let enableTransitions = false
  let currentWindowHeight
  addHandler({
    resize: function (winWidth, winHeight) {
      enableTransitions = allowMobile ? true : winWidth > PHONE_MAX
      currentWindowHeight = winHeight
      triggerPoint = isNaN(trigger) ? getOffset(trigger).top : trigger

    },
    scroll: function (scrollY, scrollX) {
      if (enableTransitions) {
        const triggerBuffer = buffer ? scrollY + (currentWindowHeight * buffer) : scrollY
        if (triggerPoint < triggerBuffer) {
          applyClass.classList.add(`scroll-${className}`)
        } else {
          if (remove) {
            applyClass.classList.remove(`scroll-${className}`)
          }
        }
      }
    }
  }, true)
}



// WEBPACK FOOTER //
// ./assets/j/util/tools.js